/**
 * Dates and money as the operator reads them, in one shape everywhere:
 * '24.08.2026' and '24.08.2026 14:05'. The shape is fixed here so two
 * screens never disagree about how a day is written.
 *
 * The language is the one the operator chose in the panel, not the one their
 * browser happens to be in. That choice lives in the i18n context, so the
 * locale-aware versions are hooks; the plain functions take the tag.
 *
 * Every date is read on Turkey's clock, whatever the browser is set to, and a
 * day an operator picks becomes the instant the API takes here too
 * (docs/15 §5).
 *
 * Nothing else in the application calls toLocale* or toISOString, or builds an
 * Intl formatter: it calls one of these.
 */
import { useI18n } from '../i18n';

// A phone number has one writer for every app (docs/15 §3).
export { formatPhone } from '@platform/ui/lib/phone';

export function formatDate(value: string | null | undefined): string {
    const parsed = parse(value);
    if (!parsed) return '—';
    return parsed.toLocaleDateString(documentTag(), DATE);
}

export function formatDateTime(value: string | null | undefined): string {
    const parsed = parse(value);
    if (!parsed) return '—';
    return `${formatDate(value)} ${parsed.toLocaleTimeString(documentTag(), TIME)}`;
}

/** The day an instant falls on in Turkey, as DateInput carries it — "2026-09-24" — or "" for none. */
export function dayOf(value: string | null | undefined): string {
    const parsed = parse(value);
    if (!parsed) return '';
    const parts = new Intl.DateTimeFormat('en', DAY_PARTS).formatToParts(parsed);
    const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((each) => each.type === type)?.value ?? '';
    return `${part('year')}-${part('month')}-${part('day')}`;
}

/** The last moment of a day in Turkey, so "ends on the 24th" means the whole 24th. */
export function endOfDay(day: string): string {
    return new Date(`${day}T23:59:59.999${TURKEY_OFFSET}`).toISOString();
}

/** The operator's language from <html lang>, not the browser's: English in a browser wrote a day month-first. */
function documentTag(): string {
    return document.documentElement.lang.toLowerCase().startsWith('en') ? 'en-GB' : 'tr-TR';
}

function parse(value: string | null | undefined): Date | null {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Money from minor units, in the operator's language. */
export function formatMinorIn(locale: string, minor: number, currency: string): string {
    return formatMoneyIn(locale, minor / 100, currency);
}

/** The same, for an amount that is already in major units — a payment's. */
export function formatMoneyIn(locale: string, amount: number, currency: string): string {
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency || 'TRY',
        // The symbol in every language: English wrote "TRY 215.00" (docs/15 §4).
        currencyDisplay: 'narrowSymbol',
    }).format(amount);
}

/** A count with its thousands grouped — "1.284" (docs/15 §3). */
export function formatCountIn(locale: string, value: number): string {
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value);
}

/**
 * Lira typed in a DecimalInput — "1499.5" — as the kuruş the API keeps, or
 * undefined for nothing typed. Read from the digits rather than multiplied, so
 * "19.99" is 1999 and not 1998.9999…; a third decimal rounds half up.
 */
export function minorFromDecimal(value: string): number | undefined {
    const match = value.trim().match(/^(\d*)(?:\.(\d*))?$/);
    if (!match || (match[1] === '' && (match[2] ?? '') === '')) return undefined;
    const fraction = (match[2] ?? '').padEnd(3, '0');
    const kurus = Number(match[1] || '0') * 100 + Number(fraction.slice(0, 2));
    return Number(fraction[2]) >= 5 ? kurus + 1 : kurus;
}

/** Kuruş as a DecimalInput carries lira — 149950 is "1499.5", 149900 is "1499" — or "" for none. */
export function decimalFromMinor(minor: number | null | undefined): string {
    if (minor === null || minor === undefined) return '';
    const whole = Math.floor(minor / 100);
    const kurus = minor % 100;
    if (kurus === 0) return String(whole);
    return `${whole}.${String(kurus).padStart(2, '0').replace(/0$/, '')}`;
}

/**
 * The formatters bound to the panel's current language.
 *
 * A hook rather than a module function because the language is a choice the
 * operator can change without reloading.
 */
export function useFormat() {
    const { locale } = useI18n();
    const tag = locale === 'tr' ? 'tr-TR' : 'en-GB';
    return {
        tag,
        date: (value: string | null | undefined) => (value ? new Date(value).toLocaleDateString(tag, DATE) : '—'),
        dateTime: (value: string | null | undefined) => {
            if (!value) return '—';
            const parsed = new Date(value);
            return `${parsed.toLocaleDateString(tag, DATE)} ${parsed.toLocaleTimeString(tag, TIME)}`;
        },
        minor: (minor: number, currency: string) => formatMinorIn(tag, minor, currency),
        money: (amount: number, currency: string) => formatMoneyIn(tag, amount, currency),
        count: (value: number) => formatCountIn(tag, value),
    };
}

// A subscription that ends at 01:30 in Istanbul ends on that day, whatever time
// zone the operator's laptop is in. Turkey has kept UTC+3 all year since 2016,
// so a day's bounds are a fixed offset — the one the API writes its dates in.
const TURKEY_TIME_ZONE = 'Europe/Istanbul';
const TURKEY_OFFSET = '+03:00';

// dd.mm.yyyy — dd/mm/yyyy in English — never a month name (docs/15 §5).
const DATE: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: TURKEY_TIME_ZONE };
const TIME: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', timeZone: TURKEY_TIME_ZONE };
// A day's parts on Turkey's clock, put back together the way DateInput carries them.
const DAY_PARTS: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: TURKEY_TIME_ZONE };
